import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { enqueueSnackbar } from "notistack";
import { Box, Link, Stack, Typography, useTheme } from "@mui/material";
import { EGInput } from "../../../../../MainComponents/Input/EGInput";
import { EGBasicSelect } from "../../../../../MainComponents/Select/EGBasicSelect";
import { EGSwitch } from "../../../../../MainComponents/Switch/Switch";
import { InvestigationButton } from "../../components/InvestigationButton";
import { useConstants } from "../../Main/useConstants";
import { interviewSchema } from "../schemas";
import { SectionedSelect } from "../../../../../MainComponents/Select/SectionedSelect";
import { enums } from "../../Main/enums";
import { useReport } from "../../../../../../context/report/ReportContext";
import { useSelector } from "react-redux";
import { IconHeader } from "../../../../../Icon/IconHader/IconHeader";
import { ParticipantManager } from "./ParticipantManager";
import { createInterview, updateInterview } from "../../../../../../apis";
import EGDatePicker from "../../../../../MainComponents/DatePicker/EGDatePicker";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import _ from "lodash";

dayjs.extend(customParseFormat);

// Lee la fecha (DD/MM/YYYY) del texto del input. Devuelve { status, date }:
// "empty" (sin dígitos), "invalid" (incompleta o imposible, p.ej. 31/02) u
// "ok" (con el dayjs de la fecha a 00:00).
const getDateFromText = (inputRef) => {
  const text = inputRef.current?.value ?? "";
  if (!/[0-9]/.test(text)) return { status: "empty", date: null };
  const match = text.match(/\d{1,2}\/\d{1,2}\/\d{4}/);
  if (!match) return { status: "invalid", date: null };
  const parsed = dayjs(match[0], "DD/MM/YYYY", true);
  return parsed.isValid()
    ? { status: "ok", date: parsed }
    : { status: "invalid", date: null };
};

// Function to check the form and return whether it is valid and what the errors are
const validateForm = (form) => {
  const { interviewed, interviewer, kind } = form;
  const errors = {};
  if (!interviewed) {
    errors.interviewed = true;
  }
  if (!interviewer) {
    errors.interviewer = true;
  }
  if (!kind) {
    errors.kind = true;
  }
  return [Object.entries(errors).length === 0 ? true : false, errors];
};

export const AddInterview = ({
  id,
  alias,
  selectedInterview,
  interviewedOpts,
  isLoading,
  setIsLoading,
  setInterviews,
  onCancel,
}) => {
  const [t] = useTranslation("investigation");
  const theme = useTheme();

  // get reportState from context
  const { state: reportState } = useReport();

  const {
    interviews: { typeInterviewOpts, connectionProviderOpts, statusOpts },
    selects: { typeUserLabels },
  } = useConstants();

  // Current user email
  const email = useSelector((state) => state.auth.email);

  // -----> Initial states <------ //
  const [interview, setInterview] = useState(interviewSchema);
  const [participants, setParticipants] = useState([]);
  const [triedToSave, setTriedToSave] = useState(false);
  // Motivo de error de la fecha: null (ok) | "invalid" (día/mes/año incompleto).
  // La hora es opcional, no genera error. Se permiten fechas pasadas.
  const [scheduledError, setScheduledError] = useState(null);
  const scheduledInputRef = useRef(null);
  const [errors, setErrors] = useState({});
  const [addInterviewObjectives, setAddInterviewObjectives] = useState(false);
  const [
    addInterviewDetailsPlaceOrConnection,
    setAddInterviewDetailsPlaceOrConnection,
  ] = useState(false);

  // Helper for reading formatted arrays from reportState
  const getFromArray = (path) => {
    const node = _.get(reportState, path);
    if (!node) return [];
    if (Array.isArray(node)) return node;
    if (Array.isArray(node?.formatted)) return node.formatted;
    if (Array.isArray(node?.raw)) return node.raw;
    return [];
  };

  const interviewerOpts = useMemo(() => {
    const { ME, RESOURCES, EXTERNAL, SYSTEM } = enums.typeUser;
    const usersWanted = [ME, RESOURCES, EXTERNAL, SYSTEM];

    let newSections = [];

    usersWanted.forEach((value) => {
      if (value === ME) {
        newSections.push({
          key: value,
          options: [
            {
              value: email,
              label: t("Interview.assign_interview_to_me"),
            },
          ],
        });
        return;
      }
      const fromArray = getFromArray(enums.typeUserRoutes[value]) || [];
      newSections.push({
        label: typeUserLabels[value],
        key: value,
        options: fromArray,
      });
    });

    // Disable party from selections
    participants.forEach((participant) => {
      const { id, from } = participant ?? {};
      const findedSectionIdx = newSections.findIndex(
        (section) => section.key === from
      );
      if (findedSectionIdx !== -1) {
        newSections[findedSectionIdx].options = newSections[
          findedSectionIdx
        ].options.map((option) => {
          if (option.value === id) {
            return { ...option, disabled: true };
          }
          return option;
        });
      }
    });

    return newSections;
  }, [participants, reportState, typeUserLabels, email]);

  // Select for add participants
  const participantsSections = useMemo(() => {
    const { ME, RESOURCES, EXTERNAL, SYSTEM } = enums.typeUser;
    const usersWanted = [ME, RESOURCES, EXTERNAL, SYSTEM];

    let newSections = [];

    usersWanted.forEach((value) => {
      if (value === ME) {
        newSections.push({
          key: value,
          options: [
            {
              value: email,
              label: t("Interview.participate_in_the_interview"),
            },
          ],
        });
        return;
      }
      const fromArray = getFromArray(enums.typeUserRoutes[value]) || [];
      newSections.push({
        label: typeUserLabels[value],
        key: value,
        options: fromArray,
      });
    });

    // Kick assigned from selections
    const { id: interviewerId, from: interviewerFrom } =
      interview.interviewer ?? {};
    const findedSectionIdx0 = newSections.findIndex(
      (section) => section.key === interviewerFrom
    );
    if (findedSectionIdx0 !== -1) {
      newSections[findedSectionIdx0].options = newSections[
        findedSectionIdx0
      ].options.map((option) => {
        if (option.value === interviewerId) {
          return { ...option, disabled: true };
        }
        return option;
      });
    }

    // Disable party from selections
    participants.forEach((participant) => {
      const { id, from } = participant ?? {};
      const findedSectionIdx = newSections.findIndex(
        (section) => section.key === from
      );
      if (findedSectionIdx !== -1) {
        newSections[findedSectionIdx].options = newSections[
          findedSectionIdx
        ].options.map((option) => {
          if (option.value === id) {
            return { ...option, disabled: true };
          }
          return option;
        });
      }
    });

    return newSections;
  }, [interview.interviewer, participants, reportState, typeUserLabels, email]);

  // It is determined whether the interview information is edited or created.
  useEffect(() => {
    if (id !== null) {
      setInterview(selectedInterview);
      setParticipants(selectedInterview.witness || []);
      setAddInterviewObjectives(!!selectedInterview.details);
      setAddInterviewDetailsPlaceOrConnection(
        !!selectedInterview.kindDetails ||
          !!selectedInterview.connectionProvider
      );
    }
  }, [selectedInterview]);

  // Validate when changes are made.
  useEffect(() => {
    if (triedToSave) {
      const [_, errors] = validateForm(interview);
      setErrors(errors);
    }
  }, [interview]);

  // onChange inputs event
  const handleChange = (valueOrEvent, name) => {
    if (name === "scheduled") {
      // Persistir el valor tal cual (dayjs válido, null o inválido).
      setInterview({
        ...interview,
        scheduled: valueOrEvent,
      });
      // Feedback en vivo sólo para el valor completo válido o el limpiado. La
      // fecha incompleta se valida en blur/guardar, porque onChange no se
      // dispara en cada tecla mientras el valor está incompleto (MUI sólo
      // publica al cambiar el estado válido↔inválido).
      if (valueOrEvent === null || valueOrEvent.isValid?.()) {
        setScheduledError(null);
      }
      return;
    }
    if (valueOrEvent?.target) {
      const { name: fieldName, type, checked, value } = valueOrEvent.target;
      setInterview({
        ...interview,
        [fieldName]: type === "checkbox" ? checked : value,
      });
      return;
    }
    if (name) {
      setInterview({
        ...interview,
        [name]: valueOrEvent,
      });
    }
  };

  // onChange select event
  const handleChangeSectionedSelect = (name, { value, section }) => {
    setInterview({ ...interview, [name]: { id: value, from: section } });
  };

  // Create interview
  const hdlCreateInterview = async (data) => {
    try {
      const resp = await createInterview(data);
      setInterviews(resp.data.interviews);
      onCancel();
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  // Update interview
  const hdlUpdateInterview = async (dataToSend) => {
    try {
      const resp = await updateInterview(dataToSend);
      setInterviews(resp.data.interviews);
      onCancel();
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const scheduledErrorMessage = scheduledError
    ? t("Interview.invalid_date_format")
    : "";

  // Valida sólo el formato de la fecha (la hora es opcional): null (ok) o "invalid".
  const getScheduledError = () => {
    const { status } = getDateFromText(scheduledInputRef);
    return status === "invalid" ? "invalid" : null;
  };

  // Validar al perder el foco (onChange no se dispara en cada tecla).
  const handleScheduledBlur = () => setScheduledError(getScheduledError());

  // onSubmit
  const handleSave = () => {
    const [isValid, errors] = validateForm(interview);
    setErrors(errors);
    setTriedToSave(true);

    const scheduledErr = getScheduledError();
    if (scheduledErr) {
      setScheduledError(scheduledErr);
      enqueueSnackbar({
        variant: "error",
        message: t("Interview.invalid_date_format"),
      });
      return;
    }

    if (!isValid) {
      enqueueSnackbar({
        variant: "error",
        message: t("resources.missing_required_inputs"),
      });
      return;
    }

    setIsLoading(true);

    // Usar el dayjs del picker si es válido; si no, recuperar la fecha a 00:00
    // desde el texto del input.
    const scheduled =
      interview.scheduled && dayjs(interview.scheduled).isValid()
        ? interview.scheduled
        : getDateFromText(scheduledInputRef).date;

    // Remove “id” from the interview object before sending it.
    const { id: _, ...cleanInterview } = interview;

    const baseData = {
      alias,
      interviews_data: {
        ...cleanInterview,
        scheduled,
        witness: participants,
        last_updated: dayjs().format("YYYY-MM-DD"),
      },
    };

    if (selectedInterview) {
      const dataToSend = {
        ...baseData,
        interviews_id: id,
      };
      hdlUpdateInterview(dataToSend);
    } else {
      hdlCreateInterview(baseData);
    }
  };

  // Validation
  const returnIsValid = (error) => {
    if (!triedToSave) return null;
    if (error) return false;
    return true;
  };

  return (
    <>
      <Box px={2} py={1}>
        <IconHeader
          iconName="outline_message_edit"
          variant="h6"
          title={
            selectedInterview !== null
              ? t("Interview.edit_interview")
              : t("Interview.add_interview")
          }
        />
        {/* Interview objectives */}
        {!!!interview.details && (
          <Link
            onClick={
              isLoading
                ? undefined
                : () => setAddInterviewObjectives((show) => !show)
            }
            underline="hover"
            display="block"
            my={0.5}
            fontWeight={600}
            sx={{
              pointerEvents: isLoading ? "none" : "auto",
              opacity: isLoading ? 0.5 : 1,
            }}
          >
            {`${addInterviewObjectives ? "-" : "+"} ${t(
              "Interview.add_interview_objectives"
            )}`}
          </Link>
        )}
        {addInterviewObjectives && (
          <EGInput
            label={`${t("Interview.interview_objectives")}: `}
            name="details"
            value={interview.details}
            onChange={handleChange}
            type="textarea"
            multiline
            rows={3}
            sx={{ mt: 1, mb: 1 }}
            disabled={isLoading}
          />
        )}
        {/* Date and participants */}
        <Box
          sx={{
            mt: 1.5,
            display: "grid",
            gap: 2,
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
              md: "1fr 1fr 1fr",
            },
          }}
        >
          <EGDatePicker
            label={`${t("Interview.interview_date")}: `}
            value={interview.scheduled ? dayjs(interview.scheduled) : null}
            onChange={(e) => handleChange(e, "scheduled")}
            onBlur={handleScheduledBlur}
            inputRef={scheduledInputRef}
            format="DD/MM/YYYY"
            isValid={scheduledError ? false : undefined}
            helperText={scheduledErrorMessage}
            disabled={isLoading}
          />
          <SectionedSelect
            label={`${t("Interview.interviewee")}: `}
            value={interview.interviewed?.id ?? ""}
            onChange={(e) => handleChangeSectionedSelect("interviewed", e)}
            sections={interviewedOpts}
            required={true}
            isValid={returnIsValid(errors?.interviewed) ?? null}
            showSuccessValidation
            showHelpText={false}
            disabled={isLoading}
          />
          <SectionedSelect
            label={`${t("Interview.interviewer")}: `}
            value={interview.interviewer?.id ?? ""}
            onChange={(e) =>
              handleChangeSectionedSelect("interviewer", e, true)
            }
            sections={interviewerOpts}
            required={true}
            isValid={returnIsValid(errors?.interviewer) ?? null}
            showSuccessValidation
            showHelpText={false}
            disabled={isLoading}
          />
        </Box>
        <Box mt={2}>
          <ParticipantManager
            participants={participants}
            setParticipants={setParticipants}
            participantsSections={participantsSections}
            interviewerId={interview.interviewer?.id}
            isLoading={isLoading}
          />
        </Box>
        {/* Interview type */}
        <Box
          sx={{
            mt: 2,
            mb: 1,
            display: "grid",
            gap: 2,
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
            },
          }}
        >
          <EGBasicSelect
            label={`${t("Interview.interview_type")}: `}
            name="kind"
            value={interview.kind}
            onChange={handleChange}
            options={typeInterviewOpts}
            isValid={returnIsValid(errors?.kind) ?? null}
            showSuccessValidation
            showHelpText={false}
            required
            disabled={isLoading}
          />
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            px={0.5}
          >
            <Stack>
              <Typography
                fontSize={16}
                fontWeight={600}
                color={theme.palette.secondary.main}
              >
                {t("Interview.recorded_interview")}
              </Typography>
              <Typography
                fontSize={12}
                fontWeight={400}
                color={theme.palette.secondary.main}
                fontStyle="italic"
              >
                {`*${t(
                  "Interview.recording_uploads_are_permitted_after_interview_has_been_saved_record"
                )}`}
              </Typography>
            </Stack>
            <EGSwitch
              size="small"
              name="record"
              checked={interview.record}
              onChange={(e) => handleChange(e, "record")}
              disabled={isLoading}
            />
          </Box>
        </Box>

        {interview.kind && (
          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: {
                xs: "1fr",
                lg: "4fr 8fr", // lg+: 2 columns (4/12 - 8/12)
              },
            }}
          >
            {!!!interview.kindDetails && (
              <Link
                onClick={
                  isLoading
                    ? undefined
                    : () =>
                        setAddInterviewDetailsPlaceOrConnection((show) => !show)
                }
                sx={{
                  gridColumn: "1 / -1",
                  pointerEvents: isLoading ? "none" : "auto",
                  opacity: isLoading ? 0.5 : 1,
                }}
                underline="hover"
                display="block"
                fontWeight={600}
              >
                {`${addInterviewDetailsPlaceOrConnection ? "-" : "+"} ${
                  interview.kind === "in-person"
                    ? t("Interview.add_location_details")
                    : t("Interview.add_meeting_access_details")
                }`}
              </Link>
            )}
            {/* Details on where the interview takes place */}
            {addInterviewDetailsPlaceOrConnection && (
              <>
                {interview.kind === "in-person" && (
                  <Box
                    sx={{
                      gridColumn: "1 / -1",
                      mt: interview.kindDetails ? 1 : -0.5,
                    }}
                  >
                    <EGInput
                      label={`${t("Interview.location_details")}: `}
                      name="kindDetails"
                      value={interview.kindDetails}
                      onChange={handleChange}
                      multiline
                      rows={3}
                      disabled={isLoading}
                    />
                  </Box>
                )}
                {interview.kind === "video-conference" && (
                  <>
                    <Box mt={interview.kindDetails ? 1 : -0.5}>
                      <EGBasicSelect
                        label={`${t("Interview.video_conferencing_service")}: `}
                        name="connectionProvider"
                        value={interview.connectionProvider}
                        onChange={handleChange}
                        options={connectionProviderOpts}
                        disabled={isLoading}
                      />
                    </Box>

                    <Box mt={interview.kindDetails ? 1 : -0.5}>
                      <EGInput
                        label={`${t("Interview.meeting_link")}: `}
                        name="kindDetails"
                        value={interview.kindDetails}
                        onChange={handleChange}
                        disabled={isLoading}
                      />
                    </Box>
                  </>
                )}
              </>
            )}
          </Box>
        )}
        {/* Action buttons */}
        <Stack
          sx={{
            alignItems: "center",
            justifyContent: { xs: "center", md: "flex-end" },
            flexFlow: "row wrap",
            mt: 1,
            mb: 1,
            px: 1,
          }}
          spacing={1}
        >
          <InvestigationButton
            label={`${t("causes.save")}`}
            variant="success"
            icon="mark_check_bold"
            onClick={handleSave}
            loading={isLoading}
          />
          <InvestigationButton
            label={`${t("causes.cancel")}`}
            icon="close_icon"
            onClick={onCancel}
            loading={isLoading}
          />
        </Stack>
      </Box>
    </>
  );
};
